/**
 * The action clock's gunfire sound (67.18): turns the match's shot and impact events, and the bullets in flight,
 * into sounds heard from the camera — shots by distance (quieter, duller, late by the speed of sound, a longer
 * tail among trees), impacts by what they hit and how fast, and near misses past the held person by the round's
 * speed and how close it came. The rules are `audio/gunfireMix.ts`, the voices `audio/gunfire.ts`. Reads the
 * match, writes nothing; silent while fast-forwarding.
 */
import { shellExplosion } from '../core/explosions';
import { EXPLOSIONS } from '../constants/explosions';
import * as THREE from 'three';
import type { Match, ActionEvent } from '../match/match';
import type { PlayerState, Vec3 } from '../types';
import { LADDER_DIORAMA } from './commandCamera';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { muzzlePoint } from '../core/bullets';
import { getPlayerWorldPosition } from '../core/gameState';
import { shotKindOf, digPartsCrossed, hearDig, LAW_HEARD, rocketPass, gl40Pass, tubeDropCrossed, hearTubeDrop, ABOVE, aboveDelay, foldsIntoRumble, rumbleOf, rumbleLevel, rumbleDecayed, hearShot, hearImpact, nearMiss, closestApproach, panOf, coverMaterial, woodsAround, metresPerSecond, reloadPartsCrossed, hearReload, hearDrone, footstepSignature, hearStep, stepsCrossed, STEP_RANGE, commanderEarPoint, tableSoftness, onTable, hearBlast, shellBlastKind, BOLT_BEATS, CLIP_PING, type BlastKind, type Heard, type ImpactMaterial, type RocketPass, type ShotKind } from '../audio/gunfireMix';
import { fogMuffle } from '../audio/weatherAhead';
import { playShot, playDig, playBackblast, playRocketPass, playTubeDrop, playImpact, playNearMiss, playReload, playStep, playBlast, playGl40Part, playClipPing, playBolt, playShellAftermath, FarRumble } from '../audio/gunfire';
import { hearVehicle, vehicleStateOf, vehicleRumbleRate, vehicleFoldsIntoRumble, type VehicleHeard, type VehicleVoiceKind } from '../audio/vehicleMix';
import { VehicleVoices, vehicleVolume } from '../audio/vehicleSound';
import { DropshipSounds } from '../audio/dropshipSounds';
import { ShellVoices, shellAftermath } from '../audio/shellVoice';
import { getStructureWorldPosition } from '../core/gameState';
import { bulletExplosion } from '../core/explosions';
import { groundMaterial } from '../core/groundMaterial';
import { weatherHere } from '../core/weather';
import { PHYS_DT, GROUND_WEAPONS, AIRSTRIKE } from '../constants';
import { weaponKin, roundsIn } from '../core/weapons';
import { DroneHum, droneSoundOn } from '../audio/droneHum';
import { dronePos } from '../core/drones';
import { CombatPulse } from '../audio/actionMusic';
import { CANNON_VOICES, cannonFiring, cannonSpin, cannonSpinTarget, cannonWhine, hearCannon, hearCannonPop, type CannonKey } from '../audio/autocannonMix';
import { AutocannonVoice, playCannonPopHere } from '../audio/autocannonSound';
import { COUNTERMEASURE_STYLE, TURRET_VOICES, hearFlare } from '../audio/countermeasureMix';
import { playFlareHiss, playFlarePops } from '../audio/countermeasureSound';
import { flightNow } from '../core/shipNow';
import { LockTone } from '../audio/lockTone';
import { lockToneFrame, shipWarningFrame, lockProgress, LOCK_TONE_VOICES, LOCK_TONE_DEFAULT, type LockToneVoice } from '../audio/lockToneMix';
import { ANTI_DROPSHIP_MISSILE_DIALS } from '../constants/weapons';
import { GUNSHIP } from '../constants/gunship';
import { gunshipSoundOf, hearGunship, mechanismNear } from '../audio/gunshipMix';
import { playGunship } from '../audio/gunshipSound';
import { LAW_THROW, DIG_CYCLE } from '../rendering/personModel';
import { defaultOn } from '../core/defaultOn';

/** The woods around the listener are re-read this often (s). */
const WOODS_EVERY = 1;

/** 65.49: the lock tone's voice — the page's `?locktone=growl|beeps|warble` (the owner trying them in play), else the default. */
let lockVoice: LockToneVoice | null = null;
function lockToneVoice(): LockToneVoice {
  return (lockVoice ??= lockToneVoiceFromUrl());
}
function lockToneVoiceFromUrl(): LockToneVoice {
  try {
    const v = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('locktone') : null;
    return v && (LOCK_TONE_VOICES as readonly string[]).includes(v) ? (v as LockToneVoice) : LOCK_TONE_DEFAULT;
  } catch { return LOCK_TONE_DEFAULT; }
}

export class ActionSound {
  private readonly lastPos = new Map<number, Vec3>();
  private readonly whizzed = new Set<number>();
  /** 67.27 (b): the drones' blades (on behind the Lab's Drone sound row) */
  private readonly hum = new DroneHum();
  /** 65.49's lock tone: the holder's seeker, and the warning a person aboard a ship hears when an enemy seeker has it. */
  private readonly seeker = new LockTone();
  private readonly shipWarning = new LockTone();
  private lockClock = 0;
  /** 67.55: the vehicles' engines and running gear (the loudest few) */
  private readonly vehicles = new VehicleVoices();
  /** 67.68: the dropships (their voices and their sorties' events) and the pod drops */
  private readonly dropships = new DropshipSounds();
  /** 67.71: each strike round's voice — its whistle in flight, its last moments (67.70's whistle before; 68.39's one-shot before that) */
  private readonly shells = new ShellVoices();
  /** 67.78: each gun's voice (point defence's mounts: a base's CIWS and C-RAM) — its table entry, its last burst (s), its barrels' spin */
  private readonly cannons = new Map<number, { key: CannonKey; voice: AutocannonVoice; last: number | null; spin: number }>();
  /** 69.58: the gunships' roof turrets, keyed by the flight (made on its first burst) */
  private readonly turrets = new Map<number, { voice: AutocannonVoice; last: number; spin: number }>();
  /** 67.28: each unit's reload as last seen — seconds into it, and its length */
  private readonly reloads = new Map<number, { elapsed: number; total: number }>();
  private woods = 0;
  private woodsIn = 0;
  private readonly ear = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  /** 67.19: how hot the fight is around the held person — the L0 music reads it */
  readonly pulse = new CombatPulse();

  constructor(private readonly match: Match, private readonly camera: THREE.Camera, private readonly muted: () => boolean) {}

  private person(): number { return PERSON_BODY_HEIGHT * personSetOf(this.match.state).k; }

  /** The listener's ear (the camera) and its right, from this frame's camera. */
  private listen(): void {
    this.camera.getWorldPosition(this.ear);
    this.camPos.copy(this.ear);   // (68.14: the camera itself, for a sound's delay from above)
    this.right.setFromMatrixColumn(this.camera.matrixWorld, 0).normalize();
    // 67.37: in the commander's view, the fight as from the camera's focus on the ground (lane D's stop), gliding with its blend
    if (this.command && this.command.blend > 0) { const e = commanderEarPoint(this.ear, this.command.focus, this.command.blend); this.ear.set(e.x, e.y, e.z); }
  }

  /**
   * 67.37: lane D's commander view for the ears — its focus on the ground, its blend (0 the shoulder … 1 the commander's
   * view) and its stop (0 the overview, 1 the squad stop, 2 the diorama); null on the ground. The host sets it each frame.
   */
  private command: { focus: Vec3; blend: number; stop: number } | null = null;
  setCommand(c: { focus: Vec3; blend: number; stop: number } | null): void { this.command = c; }
  /** 68.14: the camera's own place (the ear is lifted toward the focus; a sound's delay from above is from here). */
  private readonly camPos = new THREE.Vector3();
  /** 68.14: from above — the commander's view past half its blend (the diorama, the overview). */
  private get above(): boolean { return !!this.command && this.command.blend >= 0.5; }
  /** People from the camera to `p` (68.14: the delay's distance from above). */
  private camPeople(p: Vec3): number { return Math.hypot(p.x - this.camPos.x, p.y - this.camPos.y, p.z - this.camPos.z) / this.person(); }
  /** 68.14: from above, a sound's delay is its distance from the camera's (on the ground, the ear's: as heard). */
  private fromAbove<T extends Heard>(h: T, p: Vec3): T { return this.above ? { ...h, delay: aboveDelay(this.camPeople(p)) } : h; }
  /** 68.14: the far fight's rumble — its energy, the folded shots still on their way, the clock they arrive by, the wind's level. */
  private readonly rumble = new FarRumble();
  private rumbleEnergy = 0;
  private readonly rumbleComing: Array<{ at: number; e: number }> = [];
  private clock = 0;
  private windLevel = 0;
  /** 68.14: the wind's level where the camera is (0 … 1; the host sets it each frame): a gale over the table masks the far fight. */
  setWind(level: number): void { this.windLevel = level; }
  /** Your own sounds are yours only on the ground; from above, your unit is one of the fight. */
  private get grounded(): boolean { return !this.command || this.command.blend < 0.5; }
  /** 67.38: in a fog bank (the weather cells on and fog where the listener is): distant gunfire muffled. */
  private inFog = false;
  private air<T extends Heard>(h: T, people: number): T { return this.inFog ? fogMuffle(h, people) : h; }
  /** The diorama's tabletop softness on what is heard. */
  private table<T extends Heard>(h: T): T { return onTable(h, this.command ? tableSoftness(this.command.blend, this.command.stop, LADDER_DIORAMA) : 0) as T; }

  /** People from the listener to `p`, and its side. */
  private where(p: Vec3): { people: number; pan: number } {
    const d = Math.hypot(p.x - this.ear.x, p.y - this.ear.y, p.z - this.ear.z);
    return { people: d / this.person(), pan: panOf(this.ear, this.right, p) };
  }
  /** 67.71: a strike round's ear — `where`, and from above the delay from the camera (its last moments meet its burst's). */
  private shellEar(p: Vec3): { people: number; pan: number; delay?: number } {
    const w = this.where(p);
    return this.above ? { ...w, delay: aboveDelay(this.camPeople(p)) } : w;
  }

  /**
   * 65.49's lock tone: the holder's seeker while its missile is up (its lock on the unit: searching, tracking — the hold's share —
   * locked), in the voice the owner picks (lockToneMix.ts; `?locktone=` tries another); and, for a person riding a ship an enemy
   * seeker tracks or holds, the ship's warning. Silent when the sound is muted.
   */
  private lockSounds(dt: number, held: PlayerState | null): void {
    this.lockClock += dt;
    const lock = held?.alive ? held.missileLock : undefined;
    if (this.muted() || !lock || lock.state === 'off') this.seeker.play(null, this.lockClock);
    else this.seeker.play(lockToneFrame(lockToneVoice(), lock.state, lockProgress(lock.held, ANTI_DROPSHIP_MISSILE_DIALS.lock.hold)), this.lockClock);
    // the ship's warning: aboard a flight (its riders not yet out) that an enemy's seeker tracks or holds
    const state = this.match.state, flight = held?.alive ? state.dropships?.flights.find(f => f.riders.indexOf(held.id) >= f.out) : undefined;   // (riders before `out` are on the ground)
    let worst: 'track' | 'lock' | null = null;
    if (flight && !this.muted()) for (const p of state.players) {
      if (!p.alive || p.teamIndex === held!.teamIndex || p.missileLock?.target !== flight.id) continue;
      if (p.missileLock.state === 'lock') { worst = 'lock'; break; }
      if (p.missileLock.state === 'track') worst = 'track';
    }
    this.shipWarning.play(worst ? shipWarningFrame(worst) : null, this.lockClock);
  }

  /** 67.27 (b): each drone in the air hums where it is (the nearest few), higher in a strike's dive; silent when off or fast-forwarding. */
  private droneSounds(): void {
    const drones = (this.match.state.drones ?? []).filter(d => d.alive);
    if (!droneSoundOn() || this.muted() || drones.length === 0) { this.hum.update([]); return; }
    this.listen();
    const heard: Array<{ id: number; level: NonNullable<ReturnType<typeof hearDrone>> }> = [];
    for (const d of drones) {
      const at = this.where(d.dive?.pos ?? dronePos(d, this.match.state));
      const level = hearDrone(at.people, !!d.dive, at.pan);
      if (level) heard.push({ id: d.id, level });
    }
    this.hum.update(heard);
  }

  /**
   * 67.55: each driving body heard — its engine (running with a driver in its seat), by its speed's share of its top and its
   * throttle; its running gear on the ground's material under it (vehicleMix.ts); the loudest few voiced (VehicleVoices). From
   * above, one past the far band feeds the far rumble instead (68.14). Silent when muted or fast-forwarding.
   */
  private vehicleSounds(dt: number): void {
    const state = this.match.state;
    if (this.muted()) { this.vehicles.update([]); return; }
    this.listen();
    const heard: Array<{ id: number; kind: VehicleVoiceKind; h: VehicleHeard }> = [], seconds = this.match.actionTick * PHYS_DT;
    for (const s of state.structures ?? []) {
      const v = vehicleStateOf(s, state, seconds);
      if (!v) continue;
      const { kind, vs } = v;
      const pos = getStructureWorldPosition(s, state.planets);
      if (this.above) {
        const cp = this.camPeople(pos);
        if (vehicleFoldsIntoRumble(cp)) { this.rumbleEnergy += vehicleRumbleRate(cp, kind, vs) * dt; continue; }
      }
      const at = this.where(pos);
      const h = hearVehicle(at.people, at.pan, kind, vs);
      if (h) heard.push({ id: s.id, kind, h });
    }
    this.vehicles.volume = vehicleVolume();
    this.vehicles.update(heard);
  }

  dispose(): void { this.seeker.release(); this.shipWarning.release(); this.hum.stop(); this.vehicles.stop(); this.dropships.stop(); this.shells.stop(); for (const c of this.cannons.values()) c.voice.stop(); this.cannons.clear(); for (const t of this.turrets.values()) t.voice.stop(); this.turrets.clear(); }

  /** 67.78: a gun's entry (made on its first burst or track), keyed by the structure. */
  private cannon(id: number, key: CannonKey): { key: CannonKey; voice: AutocannonVoice; last: number | null; spin: number } {
    let c = this.cannons.get(id);
    if (!c || c.key !== key) { c?.voice.stop(); c = { key, voice: new AutocannonVoice(CANNON_VOICES[key]), last: null, spin: 0 }; this.cannons.set(id, c); }
    return c;
  }
  /**
   * 67.78: point defence's guns — each mount's voice from its bursts (`pdBurst`, the last one's time: firing while they come)
   * and its radar (`pd.tracks`: the barrels spin up while it tracks), heard from the camera like a shot; silent once
   * still and rung out, or destroyed, or fast-forwarding. Per frame a pass over the structures with the switch on.
   */
  private cannonSounds(dt: number): void {
    const state = this.match.state;
    if (!defaultOn(state.config, 'pointDefence') && this.cannons.size === 0) return;
    const now = this.match.actionTick * PHYS_DT, muted = this.muted();
    let listened = false;
    for (const s of state.structures) {
      if (s.type !== 'pointDefence' || !s.pd) continue;
      const key = s.pd.variant as CannonKey, V = CANNON_VOICES[key];
      if (!V) continue;
      const c = this.cannon(s.id, key), alive = s.health > 0;
      const firing = alive && cannonFiring(V, c.last, now), tracking = alive && (s.pd.tracks?.length ?? 0) > 0;
      c.spin = cannonSpin(V, c.spin, cannonSpinTarget(tracking, firing), dt);
      const rung = c.last === null || now - c.last > V.tail + 1;
      if (muted || (!firing && c.spin <= 0 && rung)) { if (c.voice.live) c.voice.update(null); continue; }
      if (!listened) { this.listen(); listened = true; }
      const at = getStructureWorldPosition(s, state.planets), w = this.where(at), h = hearCannon(V, w.people, w.pan, this.woods);
      if (!h) { if (c.voice.live) c.voice.update(null); continue; }
      const wh = cannonWhine(V, c.spin);
      c.voice.update({ heard: this.fromAbove(this.table(this.air(h, w.people)), at), firing, whineHz: wh.hz, whineLevel: wh.level, rate: wh.rate });
    }
  }
  /**
   * 69.58: the gunships' roof turrets — each one's voice from its bursts (`shipIntercept`, the last one's time; the turret's
   * style COUNTERMEASURE_STYLE.turret, the lean), from where its ship is now, its barrels spinning up as it fires (the sim
   * keeps no track for it); let go once rung out or its ship is gone. Its misses' self-destructing rounds are not heard: play
   * does not draw them yet. Per frame a pass over the turrets that have fired.
   */
  private turretSounds(dt: number): void {
    if (this.turrets.size === 0) return;
    const state = this.match.state, tick = this.match.actionTick, now = tick * PHYS_DT, V = TURRET_VOICES[COUNTERMEASURE_STYLE.turret], muted = this.muted();
    for (const [id, t] of this.turrets) {
      const f = state.dropships?.flights.find(x => x.id === id), at = f ? flightNow(state, f, tick)?.pos : undefined;
      const firing = !!at && cannonFiring(V, t.last, now);
      t.spin = cannonSpin(V, t.spin, cannonSpinTarget(false, firing), dt);
      if (!at || (!firing && t.spin <= 0 && now - t.last > V.tail + 1)) { t.voice.stop(); this.turrets.delete(id); continue; }
      if (muted) { if (t.voice.live) t.voice.update(null); continue; }
      this.listen();
      const w = this.where(at), h = hearCannon(V, w.people, w.pan, this.woods);
      if (!h) { if (t.voice.live) t.voice.update(null); continue; }
      const wh = cannonWhine(V, t.spin);
      t.voice.update({ heard: this.fromAbove(this.table(this.air(h, w.people)), at), firing, whineHz: wh.hz, whineLevel: wh.level, rate: wh.rate });
    }
  }
  /** 67.78: a gun's self-destructing round bursting at `at` (the host draws the airburst there): its pop, heard as a shot is. */
  cannonPop(key: CannonKey, at: Vec3): void {
    const V = CANNON_VOICES[key];
    if (!V || this.muted()) return;
    this.listen();
    const w = this.where(at), h = hearCannonPop(V, w.people, w.pan);
    if (h) playCannonPopHere(this.fromAbove(this.table(this.air(h, w.people)), at));
  }

  /**
   * 67.25: footsteps — a step at each half stride of a person's drawn gait, on the ground under them (the material query;
   * mud where rain falls on soil), wet in the rain, softer crouched, a rustle prone, louder running; near only, by the
   * step's signature (`footstepSignature`, the perception layer's hook — not read by it yet).
   */
  private footsteps(held: PlayerState | null, gaitOf: (id: number) => { phase: number; run: number } | null): void {
    const state = this.match.state, muted = this.muted();
    const seconds = this.match.actionTick * PHYS_DT;
    if (!muted) this.listen();
    for (const p of state.players) {
      const g = p.alive && !p.mount && !p.airborne ? gaitOf(p.id) : null;
      if (!g) { this.strides.delete(p.id); continue; }
      const last = this.strides.get(p.id);
      this.strides.set(p.id, g.phase);
      if (muted || last === undefined) continue;
      const n = stepsCrossed(last, g.phase);
      if (n === 0) continue;
      const at = this.where(getPlayerWorldPosition(p, state.planets)), own = p.id === held?.id && this.grounded;
      if (!own && at.people > STEP_RANGE) continue;
      const weather = weatherHere(state, p.planetIndex, p.surfaceDir, seconds);   // 62.54: the weather where the step falls
      const kind = {
        stance: p.stance === 'crouch' ? 'crouch' as const : p.stance === 'prone' ? 'prone' as const : 'stand' as const,
        run: g.run, material: groundMaterial(state, p.planetIndex, p.surfaceDir, seconds), wet: weather === 'rain' || weather === 'storm',
      };
      const h = hearStep(at.people, own, at.pan, footstepSignature(kind), !!held && p.teamIndex === held.teamIndex);   // (a teammate's at a level you hear)
      if (h) for (let i = 0; i < n; i++) playStep(kind, this.table(h));
    }
  }

  /** 67.28: the reloads — each part as a unit's reload passes it (its own state's timer); a reload cut short says no more. */
  private reloadSounds(held: PlayerState | null): void {
    const state = this.match.state, muted = this.muted();
    if (!muted) this.listen();
    for (const p of state.players) {
      const was = this.reloads.get(p.id);
      const total = p.reloadTime ?? was?.total ?? 0;
      const now = p.alive && p.reloadLeft !== undefined && p.reloadTime ? p.reloadTime - p.reloadLeft : null;
      if (!was && now === null) continue;
      if (!muted && total > 0) {
        // S7: the LAW's spent tube leaves the hands LAW_THROW.drop of the way into its reload (as the person view drops it)
        if (p.weapon && weaponKin(p.weapon) === 'rocket' && tubeDropCrossed(was?.elapsed ?? null, now, total, LAW_THROW.drop)) this.tubeDrop(p, held, 0);
        const parts = reloadPartsCrossed(was?.elapsed ?? null, now, total);
        if (parts.length) {
          const at = this.where(getPlayerWorldPosition(p, state.planets));
          const h0 = hearReload(at.people, p.id === held?.id && this.grounded, at.pan), h = h0 && this.table(h0);
          if (h) for (const part of parts) {
            if (p.weapon !== 'gl40') { playReload(part, h); continue; }
            // 65.31 / 67.55: the 40 mm's break action on the reload's beats — opened and the case tipped out, a round in, closed
            if (part === 'out') { playGl40Part('open', h); playGl40Part('caseOut', { ...h, delay: h.delay + 0.3 }); }
            else playGl40Part(part === 'in' ? 'roundIn' : 'close', h);
          }
        }
      }
      if (now === null) this.reloads.delete(p.id); else this.reloads.set(p.id, { elapsed: now, total });
    }
  }

  /**
   * S7: the LAW's back-blast — heard from LAW_HEARD.backblastPeople behind the tube (back along the rocket's flight from
   * the muzzle), so from its own side and at its own delay; and, the last rocket gone (none spare), the tube dropped at once.
   */
  private rocketLaunched(b: { pos: Vec3; vel: Vec3 }, shooter: PlayerState | undefined, controlled: number | null): void {
    const sp = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
    if (sp > 0) {
      const back = LAW_HEARD.backblastPeople * this.person() / sp;
      const at = { x: b.pos.x - b.vel.x * back, y: b.pos.y - b.vel.y * back, z: b.pos.z - b.vel.z * back };
      const w = this.where(at);
      const h = hearShot(w.people, 'rocket', false, w.pan, this.woods);
      if (h) playBackblast(this.late(this.fromAbove(this.table(this.air({ ...h, gain: h.gain * LAW_HEARD.backblastGain }, w.people)), at)));
    }
    if (shooter && (shooter.reserves?.rocket ?? 1) <= 0) this.tubeDrop(shooter, this.match.state.players.find(p => p.id === controlled) ?? null, 0.25 + this.lag);
  }

  /**
   * 65.36: a classic gun's action after its shot, heard as a reload's parts are: the Garand's empty clip pinging clear on
   * its last round (CLIP_PING); the Springfield's bolt worked on the drawn bolt's beats (BOLT_BEATS) — not after the
   * last round, when the reload opens it.
   */
  private actionAfterShot(p: PlayerState, kind: 'garand' | 'springfield', controlled: number | null): void {
    const spec = GROUND_WEAPONS[kind], left = roundsIn(p, kind, spec);
    if (kind === 'garand' ? left > 0 : left <= 0) return;
    const at = this.where(getPlayerWorldPosition(p, this.match.state.planets));
    const h0 = hearReload(at.people, p.id === controlled && this.grounded, at.pan), h = h0 && this.late(this.table(h0));
    if (!h) return;
    if (kind === 'garand') playClipPing({ ...h, delay: h.delay + CLIP_PING.after });
    else for (const part of ['open', 'shut'] as const) playBolt(part, { ...h, delay: h.delay + BOLT_BEATS[part] * spec.interval });
  }

  /** S7: the spent LAW landing beside `p` (its ground's material), `after` seconds on. */
  private tubeDrop(p: PlayerState, held: PlayerState | null, after: number): void {
    const state = this.match.state;
    const at = this.where(getPlayerWorldPosition(p, state.planets)), own = p.id === held?.id && this.grounded;
    const h = hearTubeDrop(at.people, own, at.pan);
    if (h) playTubeDrop(groundMaterial(state, p.planetIndex, p.surfaceDir, this.match.actionTick * PHYS_DT), this.table({ ...h, delay: h.delay + after }));
  }

  /** 65.38: the tick's place in its frame, as wall seconds (`tickLag`): each of its sounds starts this much later. */
  private lag = 0;
  private late<T extends { delay?: number }>(h: T): T { return this.lag > 0 ? { ...h, delay: (h.delay ?? 0) + this.lag } : h; }

  /**
   * The match's events of a tick: shots and impacts. `lag` (65.38): the tick's place in the frame that ran it, in wall
   * seconds (`tickLag`) — its sounds start that much later, so a frame's shots keep their spacing.
   */
  events(events: ActionEvent[], controlled: number | null, lag = 0): void {
    if (events.length === 0) return;
    this.listen();
    const state = this.match.state, ps = personSetOf(state);
    // 67.19: the fight's heat for the music, heard or not (fast-forwarded too)
    for (const e of events) {
      if (e.kind === 'fired') this.pulse.shot(this.where(e.bullet.pos).people, e.unitId === controlled);
      else if (e.kind === 'hitUnit') {
        if (e.targetId === controlled) this.pulse.hitTaken();
        else this.pulse.impactNear(this.where(e.point).people);
        if (e.killed && e.bullet.ownerId === controlled) this.pulse.kill();
      } else if (e.kind === 'hitGround' || e.kind === 'hitCover') this.pulse.impactNear(this.where(e.point).people);
      else if (e.kind === 'pdBurst') this.cannon(e.mountId, e.variant as CannonKey).last = this.match.actionTick * PHYS_DT;   // 67.78: the gun firing (heard or not: its gate stays true to the bursts)
      else if (e.kind === 'shipIntercept') {   // 69.58: a gunship's roof turret firing (its gate, as point defence's)
        const t = this.turrets.get(e.flight), now = this.match.actionTick * PHYS_DT;
        if (t) t.last = now; else this.turrets.set(e.flight, { voice: new AutocannonVoice(TURRET_VOICES[COUNTERMEASURE_STYLE.turret]), last: now, spin: 0 });
      }
    }
    if (this.muted()) return;
    this.lag = lag;
    for (const e of events) {
      if (e.kind === 'fired') {
        const shooter = state.players.find(p => p.id === e.unitId);
        const kind: ShotKind = shotKindOf(shooter, state);   // 65.13 / 65.14: each gun its own shot; 65.26: the .50 cal's at its seat
        const w = this.where(e.bullet.pos);
        // 68.14: from above, a light gun's shot past the far band is no crack of its own — it swells the far rumble when it arrives
        if (this.above) { const cp = this.camPeople(e.bullet.pos); if (foldsIntoRumble(cp, kind)) { this.rumbleComing.push({ at: this.clock + aboveDelay(cp) + this.lag, e: rumbleOf(cp) }); continue; } }
        const h = hearShot(w.people, kind, e.unitId === controlled && this.grounded, w.pan, this.woods);
        if (h) playShot(kind, this.late(this.fromAbove(this.table(e.unitId === controlled && this.grounded ? h : this.air(h, w.people)), e.bullet.pos)));
        if (kind === 'rocket' || kind === 'panzerfaust') this.rocketLaunched(e.bullet, shooter, controlled);
        if (shooter && (kind === 'garand' || kind === 'springfield')) this.actionAfterShot(shooter, kind, controlled);
      } else if (e.kind === 'missileFired') {   // 65.49 step 2: the anti-dropship missile's launch and its back-blast, the LAW's
        const shooter = state.players.find(p => p.id === e.unitId), w = this.where(e.missile.pos);
        const h = hearShot(w.people, 'missile', e.unitId === controlled && this.grounded, w.pan, this.woods);
        if (h) playShot('missile', this.late(this.fromAbove(this.table(e.unitId === controlled && this.grounded ? h : this.air(h, w.people)), e.missile.pos)));
        this.rocketLaunched(e.missile, shooter, controlled);
      } else if (e.kind === 'siteLaunch') {   // 69.50 step 2a: an air-defence site's missile away — the anti-dropship missile's launch, from its launcher
        const w = this.where(e.missile.pos);
        const h = hearShot(w.people, 'missile', false, w.pan, this.woods);
        if (h) playShot('missile', this.late(this.fromAbove(this.table(this.air(h, w.people)), e.missile.pos)));
        this.rocketLaunched(e.missile, undefined, controlled);
      } else if (e.kind === 'gunshipFire') {   // 69.68: a gunship's guns by their calibres and its rockets by kind (audio/gunshipMix.ts; the styles GUNSHIP_STYLE, the leans), heard from where they leave it
        const g = state.dropships?.flights.find(x => x.id === e.flight)?.gunship, w = this.where(e.from);
        const key = gunshipSoundOf(e.weapon, { chin: g?.chin ?? GUNSHIP.chinGun, side: g?.gun ?? GUNSHIP.gun });
        const h = hearGunship(key, w.people, w.pan, this.woods);
        if (h) playGunship(key, this.late(this.fromAbove(this.table(this.air(h, w.people)), e.from)), mechanismNear(w.people));
      } else if (e.kind === 'hitGround' || e.kind === 'hitCover' || e.kind === 'hitUnit' || e.kind === 'hitDrone') {
        const w = this.where(e.point);
        const speed = Math.hypot(e.bullet.vel.x, e.bullet.vel.y, e.bullet.vel.z) / Math.max(1e-9, ps.bulletSpeed);
        if (this.above && this.camPeople(e.point) > ABOVE.farBand) continue;   // 68.14: a round landing far off is lost in the rumble
        const heavy = shotKindOf(state.players.find(p => p.id === e.bullet.ownerId), state) === 'hmg';   // 65.26: a .50's round
        const h = hearImpact(w.people, speed, w.pan, heavy);
        if (!h) continue;
        const material: ImpactMaterial = e.kind === 'hitUnit' ? 'flesh' : e.kind === 'hitDrone' ? 'metal' : e.kind === 'hitGround' ? 'ground'
          : coverMaterial(state, e.planetIndex, e.point, 3 * this.person());
        playImpact(material, this.late(this.fromAbove(this.table(this.air(h, w.people)), e.point)), speed, heavy);
      } else if (e.kind === 'blast' || e.kind === 'shellImpact') {
        // 67.25a: a burst (a grenade, a rocket, a strike drone) or a gun's shell landing, heard at its distance in people
        const w = this.where(e.point);
        // 65.35: heard by its class (constants/explosions.ts) — a burst's, a gun's shell's; the turn game's other shells by their weapon
        const cls = e.kind === 'blast' ? e.explosion : shellExplosion(e.shell.weaponKey), heard = cls ? EXPLOSIONS[cls].sound : e.kind === 'shellImpact' ? shellBlastKind(e.shell.weaponKey) : 'none';
        if (heard === 'none') continue;
        const kind: BlastKind = heard;
        const h = hearBlast(w.people, kind, w.pan, this.woods);
        if (!h) continue;
        const heardAt = this.late(this.fromAbove(this.table(this.air(h, w.people)), e.point));   // (68.14: from above, with its delay from the camera)
        playBlast(kind, heardAt);
        if (cls === AIRSTRIKE.explosion) playShellAftermath(shellAftermath(w.people, Math.floor(Math.random() * 1e6)), heardAt);   // 67.71: a strike round's debris and roll
      } else if (e.kind === 'decoyPop') {   // 69.58: a ship's flare salvo — the cartridges, then the flares' hiss, from where it popped (countermeasureMix; the style the lean)
        const w = this.where(e.at), pop = hearFlare('pop', w.people, w.pan), hiss = hearFlare('hiss', w.people, w.pan);
        if (pop) playFlarePops(this.late(this.fromAbove(this.table(this.air(pop, w.people)), e.at)));
        if (hiss) playFlareHiss(this.late(this.fromAbove(this.table(this.air(hiss, w.people)), e.at)));
      } else if (e.kind === 'shipIntercept' && e.hit) {   // 69.58: the roof turret's kill — the missile dies in a small blast where it was (as point defence's)
        const w = this.where(e.at), h = hearBlast(w.people, 'small', w.pan, this.woods);
        if (h) playBlast('small', this.late(this.fromAbove(this.table(this.air(h, w.people)), e.at)));
      } else if (e.kind === 'intercepted') {
        // 67.78: a round killed in the air by point defence — the gunfire's small blast where it died
        const w = this.where(e.at), h = hearBlast(w.people, 'small', w.pan, this.woods);
        if (h) playBlast('small', this.late(this.fromAbove(this.table(this.air(h, w.people)), e.at)));
      }
    }
    this.lag = 0;
  }

  /** Each frame: the woods around the ear, and the bullets that pass the held person (or the camera, watching). */
  /** 67.25: each walker's gait phase as last heard */
  private readonly strides = new Map<number, number>();

  /** 68.14: the far fight's rumble — the folded shots arriving swell it, it dies away, the wind masks it; silent on the ground. */
  private farRumble(dt: number): void {
    this.clock += dt;
    let keep = 0;
    for (const c of this.rumbleComing) { if (c.at <= this.clock) this.rumbleEnergy += c.e; else this.rumbleComing[keep++] = c; }
    this.rumbleComing.length = keep;
    this.rumbleEnergy = rumbleDecayed(this.rumbleEnergy, dt);
    this.rumble.set(this.above && !this.muted() ? rumbleLevel(this.rumbleEnergy, this.windLevel) : 0);
  }

  /** 66.8: each digger's strokes on the match's clock — counted from the tick its dig was first seen (DIG_CYCLE's pace). */
  private readonly digs = new Map<number, { start: number; strokes: number }>();
  /**
   * 66.8: the dig heard — each stroke's bite and its spoil landing (gunfireMix DIG_HEARD), by the ground's material under
   * the digger (its dig's), as a footstep is heard, further. The strokes run on the match's clock from the tick its dig
   * first appears, at the person view's pace (DIG_CYCLE.seconds), so they hold still when the match does.
   */
  private digSounds(held: PlayerState | null): void {
    const state = this.match.state, now = this.match.actionTick * PHYS_DT, muted = this.muted();
    for (const p of state.players) {
      const was = this.digs.get(p.id);
      if (!p.alive || !p.digAt) { if (was) this.digs.delete(p.id); continue; }
      const d = was ?? { start: now, strokes: 0 };
      const strokes = (now - d.start) / DIG_CYCLE.seconds;
      const parts = was ? digPartsCrossed(d.strokes, strokes) : [];
      this.digs.set(p.id, { start: d.start, strokes });
      if (muted || parts.length === 0) continue;
      const at = this.where(getPlayerWorldPosition(p, state.planets)), own = p.id === held?.id && this.grounded;
      const h = hearDig(at.people, own, at.pan);
      if (h) for (const part of parts) playDig(p.digAt.dig.material, part, this.table(this.air(h, at.people)));
    }
  }

  /** `gaitOf`: a person's drawn gait (PersonView.gait), so the steps land with the feet; absent, no footsteps. */
  frame(dt: number, held: PlayerState | null, gaitOf?: (id: number) => { phase: number; run: number } | null): void {
    this.pulse.tick(dt);
    const state = this.match.state, bullets = state.bullets ?? [];
    this.reloadSounds(held);
    if (gaitOf) this.footsteps(held, gaitOf);
    this.digSounds(held);
    this.droneSounds();
    this.vehicleSounds(dt);
    this.cannonSounds(dt);   // 67.78
    this.turretSounds(dt);   // 69.58
    this.lockSounds(dt, held);   // 65.49
    if (this.match.state.dropships || this.match.state.pods || this.match.state.shipPads) { const muted = this.muted(); if (!muted) this.listen(); this.dropships.frame(this.match.state, this.match.actionTick, p => this.where(p), muted, dt); }   // 67.68
    if (this.match.state.airstrikes || this.shells.live) { const muted = this.muted(); if (!muted) this.listen(); this.shells.frame(this.match.state, p => this.shellEar(p), muted, dt); }   // 67.71
    this.farRumble(dt);
    const live = new Set<number>();
    if (!this.muted()) {
      this.listen();
      // 67.38: the fog bank where the listener is (lane A's weather cells; off: never)
      const cfg = state.config as typeof state.config & { weatherCells?: boolean };
      const lp = held?.planetIndex ?? state.players.find(p => p.alive)?.planetIndex ?? 0, pl = state.planets[lp];
      this.inFog = cfg.weatherCells === true && !!pl && weatherHere(state, lp, { x: this.ear.x - pl.position.x, y: this.ear.y - pl.position.y, z: this.ear.z - pl.position.z }, this.match.actionTick * PHYS_DT) === 'fog';
      if ((this.woodsIn -= dt) <= 0) {
        this.woodsIn = WOODS_EVERY;
        const pi = held?.planetIndex ?? state.players.find(p => p.alive)?.planetIndex ?? 0;
        this.woods = woodsAround(state, pi, this.ear, 15 * this.person());
      }
      const ps = personSetOf(state);
      const head = held?.alive ? muzzlePoint(held, state.planets, ps.eyeHeight, ps) : { x: this.ear.x, y: this.ear.y, z: this.ear.z };
      const person = this.person();
      for (const b of bullets) {
        live.add(b.id);
        const prev = this.lastPos.get(b.id) ?? { x: b.pos.x - b.vel.x * Math.min(b.age, dt), y: b.pos.y - b.vel.y * Math.min(b.age, dt), z: b.pos.z - b.vel.z * Math.min(b.age, dt) };
        this.lastPos.set(b.id, b.pos);
        if (this.whizzed.has(b.id) || b.ownerId === held?.id || !this.grounded) continue;   // (67.37: no rounds past your head from the sky)
        const c = closestApproach(prev, b.pos, head);
        if (c.t >= 1) continue;   // still coming: it passes on a later frame (or hits)
        const speed = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
        if (b.blast) {   // S7: a rocket — no crack (subsonic): its motor's hiss going by
          const r0 = rocketPass(c.dist / person, panOf(this.ear, this.right, b.pos)), r = r0 && bulletExplosion(b) === 'gl40' ? gl40Pass(r0) : r0;   // (65.31: a 40 mm round has no motor's hiss; by its class, so 65.43's arming rounds keep their own)
          if (r) { this.whizzed.add(b.id); playRocketPass(r); this.pulse.nearMiss(c.dist / person); }
          continue;
        }
        const n = nearMiss(metresPerSecond(speed, person), c.dist / person, panOf(this.ear, this.right, b.pos), shotKindOf(state.players.find(p => p.id === b.ownerId), state) === 'hmg');   // 65.26: a .50's round
        if (n) { this.whizzed.add(b.id); playNearMiss(n); this.pulse.nearMiss(c.dist / person); }
      }
    } else for (const b of bullets) { live.add(b.id); this.lastPos.set(b.id, b.pos); }
    for (const id of this.lastPos.keys()) if (!live.has(id)) { this.lastPos.delete(id); this.whizzed.delete(id); }
  }
}
